/**
 * Self-contained "share by link" encoding for the client-only viewer.
 *
 * A share link carries the entire workspace (and optionally the current
 * view + an active diff) inside the URL **fragment** — e.g.
 *
 *     https://viewer.example/#s=N4Ig…
 *
 * The fragment is deliberate: browsers never send it to the server, so a
 * shared diagram's source never touches the static host's logs or CDN.
 * The whole feature is pure client-side and needs no backend.
 *
 * Trust model: a decoded link is treated exactly like a user-uploaded
 * workspace — arbitrary `.arch` *text*, never executed. To stay robust
 * against hand-crafted / decompression-bomb payloads, `decodeShareDoc`
 * caps the decompressed size and file count and validates every field;
 * any malformed input collapses to `null` (→ fall back to demo/storage).
 *
 * This module is intentionally DOM-free so it unit-tests in isolation;
 * the browser glue (reading `location.hash`, writing the clipboard)
 * lives in the callers.
 */

import {
  compressToEncodedURIComponent,
  decompressFromEncodedURIComponent,
} from "lz-string";
import type { Vfs } from "@archlang/viewer-core";
import { type Assets, bytesToBase64, base64ToBytes } from "./workspaceAssets";
import type { PersistedViewport, PersistedViewerState } from "./viewerStorage";

/** Bumped when the envelope shape changes incompatibly. Older `v` → reject. */
export const SHARE_VERSION = 1;
/** Query/hash key holding the compressed payload: `#s=…`. */
export const SHARE_PARAM = "s";

/**
 * Encoded-length tiers. URLs are universally safe to ~2KB, fine in
 * browsers to ~8KB, and start getting truncated by chat apps / issue
 * trackers / proxies well before the browser's own (much higher) limit.
 *  - `safe` : copy silently.
 *  - `long` : copy, but warn it may break in some tools. The bundled demo —
 *             a full multi-file showcase (~46KB encoded and still growing as
 *             new language features get demoed) — lives here: a working browser
 *             URL that a few aggressive chat proxies might truncate. The ceiling
 *             carries headroom above the demo so it stays a share link, not a
 *             refused download, as the showcase grows. Hash-fragment payloads
 *             (`#s=`) never hit a server, so the browser URL limit (~MBs), not a
 *             header cap, is the real bound — 60KB is comfortably safe there.
 *  - `huge` : refuse — offer a file download instead.
 */
export const SHARE_SIZE = { safe: 8_000, long: 60_000 } as const;
export type ShareSizeTier = "safe" | "long" | "huge";

export function shareSizeTier(encodedLength: number): ShareSizeTier {
  if (encodedLength <= SHARE_SIZE.safe) return "safe";
  if (encodedLength <= SHARE_SIZE.long) return "long";
  return "huge";
}

// Decode-time guards against crafted payloads.
const MAX_DECODED_CHARS = 8_000_000; // ~8 MB of JSON after decompression
const MAX_FILES = 4_000;

export interface ShareSelection {
  readonly module?: string;
  readonly edge?: string;
  readonly iface?: string;
}

export interface ShareView {
  readonly viewport?: PersistedViewport;
  readonly sel?: ShareSelection;
}

export interface ShareCompare {
  readonly files: ReadonlyArray<readonly [string, string]>;
  readonly label?: string;
  readonly swapped?: boolean;
}

export interface ShareDoc {
  /** Workspace files as `[path, content]` pairs (a serialized Vfs). */
  readonly files: ReadonlyArray<readonly [string, string]>;
  /** Binary assets (images) as `[path, base64]` pairs. */
  readonly assets?: ReadonlyArray<readonly [string, string]>;
  readonly label?: string;
  /** Focal/pan/zoom + selection, when sharing "this view". */
  readonly view?: ShareView;
  /** The other side of an active diff, when sharing from compare mode. */
  readonly compare?: ShareCompare;
}

/** Result of a clipboard share attempt, surfaced inline in the menu. */
export interface ShareOutcome {
  readonly ok: boolean;
  readonly message: string;
}

interface Envelope extends ShareDoc {
  readonly v: number;
}

// ---------------------------------------------------------------------------
// Vfs <-> entries
// ---------------------------------------------------------------------------

export function vfsToEntries(vfs: Vfs): Array<[string, string]> {
  return [...vfs.entries()];
}

export function entriesToVfs(
  entries: ReadonlyArray<readonly [string, string]>,
): Vfs {
  return new Map(entries);
}

/** Assets → `[path, base64]` pairs for the share envelope. */
export function assetsToShareEntries(assets: Assets): Array<[string, string]> {
  return [...assets].map(([path, bytes]) => [path, bytesToBase64(bytes)]);
}

/** `[path, base64]` pairs → an {@link Assets} bytes map. */
export function shareEntriesToAssets(
  entries: ReadonlyArray<readonly [string, string]>,
): Assets {
  return new Map(entries.map(([path, b64]) => [path, base64ToBytes(b64)]));
}

// ---------------------------------------------------------------------------
// Encode / decode
// ---------------------------------------------------------------------------

export function encodeShareDoc(doc: ShareDoc): string {
  const envelope: Envelope = { v: SHARE_VERSION, ...doc };
  return compressToEncodedURIComponent(JSON.stringify(envelope));
}

/**
 * Decode a compressed payload back into a validated `ShareDoc`, or `null`
 * for anything malformed, oversized, or version-mismatched. Never throws.
 */
export function decodeShareDoc(encoded: string): ShareDoc | null {
  if (!encoded) return null;
  let json: string | null;
  try {
    json = decompressFromEncodedURIComponent(encoded);
  } catch {
    return null;
  }
  if (!json || json.length === 0 || json.length > MAX_DECODED_CHARS) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return null;
  }
  return normalizeEnvelope(parsed);
}

/**
 * Parse a `#s=…` fragment (leading `#` optional); other hash params are
 * ignored. Extracts the raw value by hand rather than via
 * `URLSearchParams` on purpose: lz-string's URI-safe alphabet includes
 * `+`, and `URLSearchParams.get` would decode `+` → space and corrupt the
 * payload. The value is already URI-component-safe, so it's passed to the
 * decoder verbatim.
 */
export function parseShareFromHash(hash: string): ShareDoc | null {
  if (!hash) return null;
  const prefix = `${SHARE_PARAM}=`;
  for (const part of hash.replace(/^#/, "").split("&")) {
    if (part.startsWith(prefix)) return decodeShareDoc(part.slice(prefix.length));
  }
  return null;
}

/** Compose a shareable URL from a base (`origin + pathname`) and a doc. */
export function buildShareUrl(base: string, doc: ShareDoc): string {
  return `${base}#${SHARE_PARAM}=${encodeShareDoc(doc)}`;
}

// ---------------------------------------------------------------------------
// View <-> persisted viewer state
// ---------------------------------------------------------------------------

/**
 * Map a shared view onto the same shape `viewerStorage` produces, so the
 * boot path can treat a share link and restored localStorage state
 * identically. Returns `null` when the doc carries no view.
 */
export function shareViewToViewerState(
  doc: ShareDoc,
): PersistedViewerState | null {
  const view = doc.view;
  if (!view) return null;
  return {
    viewport: view.viewport ?? null,
    selectedModuleId: view.sel?.module ?? null,
    selectedEdgeId: view.sel?.edge ?? null,
    selectedInterfaceKey: view.sel?.iface ?? null,
    sidebarCollapsed: null,
    sidebarTab: null,
  };
}

// ---------------------------------------------------------------------------
// Validation helpers
// ---------------------------------------------------------------------------

function normalizeEnvelope(value: unknown): ShareDoc | null {
  if (typeof value !== "object" || value === null) return null;
  const env = value as Record<string, unknown>;
  if (env.v !== SHARE_VERSION) return null;

  const files = normalizeFiles(env.files);
  if (!files) return null;

  const doc: {
    files: Array<[string, string]>;
    assets?: Array<[string, string]>;
    label?: string;
    view?: ShareView;
    compare?: ShareCompare;
  } = { files };

  const assets = normalizeAssets(env.assets);
  if (assets) doc.assets = assets;

  if (typeof env.label === "string") doc.label = env.label;

  const view = normalizeView(env.view);
  if (view) doc.view = view;

  const compare = normalizeCompare(env.compare);
  if (compare) doc.compare = compare;

  return doc;
}

function normalizeFiles(value: unknown): Array<[string, string]> | null {
  if (!Array.isArray(value)) return null;
  if (value.length === 0 || value.length > MAX_FILES) return null;
  const out: Array<[string, string]> = [];
  for (const entry of value) {
    if (!Array.isArray(entry) || entry.length !== 2) return null;
    const [path, content] = entry;
    if (typeof path !== "string" || typeof content !== "string") return null;
    out.push([path, content]);
  }
  return out;
}

/** Validate the optional `[path, base64]` assets array. A malformed assets
 *  field is dropped (returns null) rather than failing the whole decode — the
 *  source still loads, just without its images. */
function normalizeAssets(value: unknown): Array<[string, string]> | null {
  if (!Array.isArray(value)) return null;
  if (value.length === 0 || value.length > MAX_FILES) return null;
  const out: Array<[string, string]> = [];
  for (const entry of value) {
    if (!Array.isArray(entry) || entry.length !== 2) return null;
    const [path, b64] = entry;
    if (typeof path !== "string" || typeof b64 !== "string") return null;
    out.push([path, b64]);
  }
  return out;
}

function normalizeView(value: unknown): ShareView | null {
  if (typeof value !== "object" || value === null) return null;
  const v = value as Record<string, unknown>;
  const view: { viewport?: PersistedViewport; sel?: ShareSelection } = {};

  const viewport = normalizeViewport(v.viewport);
  if (viewport) view.viewport = viewport;

  const sel = normalizeSelection(v.sel);
  if (sel) view.sel = sel;

  return view.viewport || view.sel ? view : null;
}

function normalizeViewport(value: unknown): PersistedViewport | null {
  if (typeof value !== "object" || value === null) return null;
  const v = value as Record<string, unknown>;
  if (!Array.isArray(v.focalPath)) return null;
  if (!v.focalPath.every((s) => typeof s === "string")) return null;
  if (typeof v.zoom !== "number" || !Number.isFinite(v.zoom)) return null;
  const center = v.center as Record<string, unknown> | undefined;
  if (
    typeof center?.x !== "number" ||
    typeof center?.y !== "number" ||
    !Number.isFinite(center.x) ||
    !Number.isFinite(center.y)
  ) {
    return null;
  }
  return {
    focalPath: v.focalPath as string[],
    center: { x: center.x, y: center.y },
    zoom: v.zoom,
  };
}

function normalizeSelection(value: unknown): ShareSelection | null {
  if (typeof value !== "object" || value === null) return null;
  const v = value as Record<string, unknown>;
  const sel: { module?: string; edge?: string; iface?: string } = {};
  if (typeof v.module === "string") sel.module = v.module;
  if (typeof v.edge === "string") sel.edge = v.edge;
  if (typeof v.iface === "string") sel.iface = v.iface;
  return sel.module || sel.edge || sel.iface ? sel : null;
}

function normalizeCompare(value: unknown): ShareCompare | null {
  if (typeof value !== "object" || value === null) return null;
  const v = value as Record<string, unknown>;
  const files = normalizeFiles(v.files);
  if (!files) return null;
  const compare: { files: Array<[string, string]>; label?: string; swapped?: boolean } = {
    files,
  };
  if (typeof v.label === "string") compare.label = v.label;
  if (typeof v.swapped === "boolean") compare.swapped = v.swapped;
  return compare;
}
